import { SaleLotType } from "constants/saleLots";
import { EIDPattern } from "constants/scanner";

import { formatWeightKg } from "lib";

import { getAuctionPenDisplayName } from "lib/auctionPens";
import { getConsignmentCode } from "lib/consignments";
import {
  getAverageWeightFormatted,
  getCombinedLotNumber,
  getMarkDetails,
} from "lib/saleLot";
import { formatSaleLotOverflow } from "lib/saleLot/formatSaleLotOverflow";

import { getAuctionPenById } from "./auctionPens/basic";
import { getBusinessById } from "./businesses";
import { getConsignmentById } from "./consignments";
import { selectAgencyByConsignmentIdLookup } from "./consignmentsCalculated";
import {
  getIsFetchingConsignments,
  getIsFetchingSaleLots,
  getIsFetchingScans,
} from "./root";
import { getSaleLotById } from "./saleLots";
import {
  getConsignmentIdByEid,
  getSaleLotTypeIdByEid,
  getScanByEid,
  getScanByNlisId,
} from "./scanners";

export const scanRowWeightTypes = {
  SINGLE: "single",
  AVERAGE: "average",
};

export function selectScanRowData(
  state,
  { destinationConsignmentId, eidOrNlisId, showDuplicate },
) {
  const isEid = EIDPattern.SIXTEEN_DIGIT_PATTERN.test(eidOrNlisId);

  const scan = isEid
    ? getScanByEid(eidOrNlisId)(state)
    : getScanByNlisId(eidOrNlisId)(state);
  const eid = (scan && scan.EID) || (isEid && eidOrNlisId);

  let derivedNlisId = isEid ? "" : eidOrNlisId;

  let isDuplicate = false;
  let isEidTaken = false;
  let isSaleyardScanned = false;

  let vendorName = "";
  let vendorCode = "";
  let lotNumber = "";
  let buyerName = "";
  let buyerWayName = "";
  let nlisPic = "";
  let penName = "";
  let agencyName = "";
  let marks = "";
  let weight = "";

  if (scan) {
    derivedNlisId = isEid ? scan.animal?.nlis_id : eidOrNlisId;
    nlisPic = scan.current_pic;

    const consignmentId = getConsignmentIdByEid(eid)(state);
    const agency =
      selectAgencyByConsignmentIdLookup(state)[consignmentId] || {};
    const saleLotType = getSaleLotTypeIdByEid(eid)(state);
    const consignment = getConsignmentById(consignmentId)(state) || {};
    const vendor = getBusinessById(consignment.vendor_id)(state) || {};
    vendorName = vendor.name;
    vendorCode = vendorName ? getConsignmentCode(consignment) : "";
    agencyName = agency.name;

    isEidTaken = Boolean(consignment.nlis_take_file_status);

    if (saleLotType === SaleLotType.SALEYARD_SCAN) {
      isSaleyardScanned = true;
      isDuplicate = consignmentId !== destinationConsignmentId;
    } else if (showDuplicate) {
      const saleLot = getSaleLotById(scan.sale_lot_id)(state) || {};
      const buyer = getBusinessById(saleLot.buyer_id)(state) || {};
      const auctionPen = getAuctionPenById(saleLot.auction_pen_id)(state);
      const { overflowPen } = saleLot;
      penName = overflowPen
        ? `${getAuctionPenDisplayName(auctionPen, "")} ${formatSaleLotOverflow(
            overflowPen,
          )}`
        : getAuctionPenDisplayName(auctionPen, "");
      buyerName = buyer.name;
      buyerWayName = saleLot.buyer_way?.name;
      lotNumber = getCombinedLotNumber(saleLot);
      isDuplicate = true;
      marks = getMarkDetails(saleLot);
      weight = scan.time_weighed
        ? {
            value: `${formatWeightKg(scan.total_mass_grams, true)}`,
            type: scanRowWeightTypes.SINGLE,
          }
        : {
            value: `${getAverageWeightFormatted(saleLot)} kg`,
            type: scanRowWeightTypes.AVERAGE,
          };
    }
  }

  return {
    agencyName,
    buyerName,
    buyerWayName,
    eid,
    isEidTaken,
    isDuplicate,
    isSaleyardScanned,
    lotNumber,
    derivedNlisId,
    nlisPic,
    marks,
    penName,
    derivedSyncing:
      getIsFetchingSaleLots(state) ||
      getIsFetchingScans(state) ||
      getIsFetchingConsignments(state),
    vendorName,
    vendorCode,
    derivedWeight: weight,
  };
}
